import { TranslateService } from '@ngx-translate/core';
import { CaseStatus } from '../../enums/case-status.enum';
import { UserStatus } from '../../enums/user-status.enum';
import { AppointmentStatus } from '../../enums/appointment-status.enum';
import { ChatStatus } from '../../enums/chat-status.enum';

export class StatusUtils {
  private static translateService: TranslateService;
  static async initializeTranslateService(service: TranslateService) {
    this.translateService = service;
  }

  // CASE STATUS
  private static readonly STATUS_TRANSLATION_KEYS: { [key: number]: string } = {
    [CaseStatus.AIAnalysingError]: 'errorAIAnalysingDesc',
    [CaseStatus.Open]: 'open',
    [CaseStatus.AIAnalysing]: 'AIAnalysing',
    [CaseStatus.AIAnalysingComplete]: 'AIAnalysingComplete',
    [CaseStatus.WaitingConsultation]: 'waitingConsultation',
    [CaseStatus.ReportSent]: 'reportIssued',
    [CaseStatus.Closed]: 'closed',
    [CaseStatus.ReferralRequired]: 'referralRequired',
  };

  private static readonly CASE_STATUS_DETAILS: {
    [key in CaseStatus]: { class: string; text: string };
  } = {
    [CaseStatus.AIAnalysingError]: {
      class: 'badge-danger',
      text: 'errorAIAnalysingDesc',
    },
    [CaseStatus.Open]: { class: 'badge-light-primary', text: 'open' },
    [CaseStatus.AIAnalysing]: {
      class: 'badge-light-dark',
      text: 'AIAnalysing',
    },
    [CaseStatus.AIAnalysingComplete]: {
      class: 'badge-light-success',
      text: 'AIAnalysingComplete',
    },
    [CaseStatus.WaitingConsultation]: {
      class: 'badge-light-warning',
      text: 'waitingConsultation',
    },
    [CaseStatus.ReportSent]: {
      class: 'badge-light-info',
      text: 'reportIssued',
    },
    [CaseStatus.Closed]: { class: 'badge-light-danger', text: 'closed' },
    [CaseStatus.ReferralRequired]: {
      class: 'badge-light-danger',
      text: 'referralRequired',
    },
  };

  static getCaseStatusClass(status: CaseStatus): string {
    return this.CASE_STATUS_DETAILS[status]?.class;
  }

  static getCaseStatusText(status: CaseStatus): string {
    const translationKey = this.STATUS_TRANSLATION_KEYS[status];
    if (translationKey && this.translateService) {
      return this.translateService.instant(translationKey);
    }
    return '';
  }

  // USER STATUS
  private static readonly USER_TRANSLATION_KEYS: { [key: string]: string } = {
    [UserStatus.NewAccount]: 'newAccount',
    [UserStatus.Active]: 'active',
    [UserStatus.DeActive]: 'deActive',
    [UserStatus.Blocked]: 'blocked',
    [UserStatus.Deleted]: 'deleted',
    [UserStatus.None]: 'none',
  };

  private static readonly USER_STATUS_DETAILS: {
    [key in UserStatus]: { class: string; text: string };
  } = {
    [UserStatus.None]: { class: 'badge-light-default', text: 'none' },
    [UserStatus.NewAccount]: {
      class: 'badge-light-primary',
      text: 'newAccount',
    },
    [UserStatus.Active]: { class: 'badge-light-success', text: 'active' },
    [UserStatus.DeActive]: { class: 'badge-light-danger', text: 'deActive' },
    [UserStatus.Blocked]: { class: 'badge-light-secondary', text: 'blocked' },
    [UserStatus.Deleted]: { class: 'badge-light-default', text: 'deleted' },
  };

  static getUserStatusClass(status: number): string {
    return this.USER_STATUS_DETAILS[status]?.class;
  }

  static getUserStatusText(status: number): string {
    const translationKey = this.USER_TRANSLATION_KEYS[status];
    if (translationKey && this.translateService) {
      return this.translateService.instant(translationKey);
    }
    return '';
  }

  // APPOINTMENT STATUS
  private static readonly APPOINTMENT_STATUS_TRANSLATION_KEYS: {
    [key: number]: string;
  } = {
    [AppointmentStatus.Waiting]: 'waiting',
    [AppointmentStatus.Completed]: 'completed',
  };

  private static readonly APPOINTMENT_STATUS_DETAILS: {
    [key in AppointmentStatus]: { class: string; text: string };
  } = {
    [AppointmentStatus.Waiting]: {
      class: 'badge-light-primary',
      text: 'waiting',
    },
    [AppointmentStatus.Completed]: {
      class: 'badge-light-success',
      text: 'completed',
    },
  };

  static getAppointmentStatusClass(status: AppointmentStatus): string {
    return this.APPOINTMENT_STATUS_DETAILS[status]?.class;
  }

  static getAppointmentStatusText(status: AppointmentStatus): string {
    const translationKey = this.APPOINTMENT_STATUS_TRANSLATION_KEYS[status];
    if (translationKey && this.translateService) {
      return this.translateService.instant(translationKey);
    }
    return '';
  }

  // FILTER STATUS
  private static readonly FILTER_CASE_STATUSES: {
    [key in CaseStatus]?: { id: string; title: string };
  } = {
    [CaseStatus.Open]: { id: '1', title: 'Open' },
    [CaseStatus.AIAnalysing]: {
      id: '2',
      title: 'AI Analysing',
    },
    [CaseStatus.AIAnalysingComplete]: {
      id: '3',
      title: 'AI Analysing Complete',
    },
    [CaseStatus.WaitingConsultation]: {
      id: '4',
      title: 'Waiting Consultation',
    },
    [CaseStatus.ReportSent]: { id: '5', title: 'Report Issued' },
    [CaseStatus.Closed]: { id: '6', title: 'Closed' },
    [CaseStatus.ReferralRequired]: { id: '7', title: 'Referral Required' },
  };

  static getFilterCaseStatuses(): Array<{ id: string; title: string }> {
    return Object.values(this.FILTER_CASE_STATUSES);
  }

  private static readonly WORKLIST_CASE_STATUSES: {
    [key in CaseStatus]?: { id: string; title: string };
  } = {
    [CaseStatus.Open]: { id: '1', title: 'Waiting' },
    [CaseStatus.AIAnalysing]: {
      id: '2',
      title: 'Completed',
    },
  };

  static getFilterWorkStatuses(): Array<{ id: string; title: string }> {
    return Object.values(this.WORKLIST_CASE_STATUSES);
  }

  // CHAT STATUS
  private static readonly CHAT_STATUS_TRANSLATION_KEYS: {
    [key: number]: string;
  } = {
    [ChatStatus.MessageFromGp]: 'messageFromGp',
    [ChatStatus.MessageFromDermatologist]: 'messageFromDermatologist',
    [ChatStatus.NoMessage]: 'noMessage',
  };

  private static readonly CHAT_STATUS_DETAILS: {
    [key in ChatStatus]: { class: string; text: string };
  } = {
    [ChatStatus.NoMessage]: {
      class: 'badge-secondary',
      text: 'noMessage',
    },
    [ChatStatus.MessageFromDermatologist]: {
      class: 'badge-warning',
      text: 'messageFromDermatologist',
    },
    [ChatStatus.MessageFromGp]: {
      class: 'badge-info',
      text: 'messageFromGp',
    },
  };

  static getChatStatusClass(status: ChatStatus): string {
    return this.CHAT_STATUS_DETAILS[status]?.class;
  }

  static getChatStatusText(status: ChatStatus): string {
    const translationKey = this.CHAT_STATUS_TRANSLATION_KEYS[status];
    if (translationKey && this.translateService) {
      return this.translateService.instant(translationKey);
    }
    return '';
  }
}
